'use client';

import PriveeAcceptInvitationModal from '@/app/reserve/AcceptInvitationModal/PriveeAcceptInvitationModal';
import { useModal } from '@/components/hook/modal';
import { useFlags } from '@/components/hook/flags';
import { useLoggedInUser } from '@/components/hook/user';
import {
  isPriveeInvited,
  isPriveeMember,
  isPriveePending
} from '@/util/user_client';
import { FLAG_KEYS } from '@/const/flags';

/**
 * Single source of truth for "should this user see the Réservé waitlist
 * join CTA?" — used by the hero, the bottom-of-PLP CTA, and the product-
 * card click gate so they all stay in sync. Returns false for logged-out,
 * Réservé members, pending, invited, already-interested, or when the
 * WAITLIST_TO_UPGRADE flag is off.
 */
export function useIsPriveeWaitlistEligible(): boolean {
  const user = useLoggedInUser();
  const { value: flagValues } = useFlags();

  return (
    !!user &&
    !isPriveeMember(user) &&
    !isPriveePending(user) &&
    !isPriveeInvited(user) &&
    user.isInterestedInPrivee !== true &&
    Boolean(flagValues[FLAG_KEYS.WAITLIST_TO_UPGRADE])
  );
}

/**
 * Returns a function that opens PriveeAcceptInvitationModal in 'waitlist'
 * mode — used on /privee (waitlist-eligible variant) and /collection/reserve
 * (PriveeInterestHero, when WAITLIST_TO_UPGRADE flag is on).
 *
 * The waitlist modal never shows the initiation-fee row, so callers don't
 * need to thread the fee prop through.
 */
export function useOpenWaitlistModal() {
  const { showModal, closeModal } = useModal();

  return (priveePriceStr: string) => {
    showModal(
      <PriveeAcceptInvitationModal
        priveePriceStr={priveePriceStr}
        priveeInitFeeStr={null}
        variant='waitlist'
        onClose={() => closeModal()}
      />
    );
  };
}
